import React, {
    MouseEventHandler, ReactElement, ReactNode, useEffect, useState,
} from "react";
import Alert, { AlertVariantType } from "../theme/alert/Alert";
import { useDebounce } from "../../debouncer";

interface Props {
    shouldShow: boolean;
    title?: string | ReactElement | (string | ReactElement)[];
    variant?: AlertVariantType;
    children?: ReactNode;
    seconds?: number;
    onClose?: () => void;
    className?: string;
    alertClassName?: string;
    titleClassName?: string;
    actionText?: ReactNode;
    onActionClick?: MouseEventHandler;
    prefixIcon?: ReactNode;
    actionButton?: ReactNode,

}

const DEFAULT_SECONDS = 5;
export default function TimedMessage({
    children, seconds, onClose, variant, title, shouldShow,
    className, alertClassName, titleClassName, actionText, onActionClick, prefixIcon, actionButton,
}: Props): ReactElement | null {
    const [isShowing, setIsShowing] = useState(shouldShow);

    const onHide = useDebounce(() => {
        onClose?.();
    }, (seconds ?? DEFAULT_SECONDS) * 1000, [seconds]);

    useEffect(() => {
        setIsShowing(shouldShow);
        if (shouldShow) {
            onHide();
        }
    }, [shouldShow]);

    const handleClose = () => {
        setIsShowing(false);
        onClose?.();
    };

    if (!isShowing) {
        return null;
    }

    return (
        <div className={`fixed-top m-auto w-50 mt-4 alert-floating ${className}`}>
            <Alert
                onClose={handleClose}
                variant={variant as AlertVariantType}
                className={`d-flex justify-content-between ${alertClassName}`}
                actionText={actionText}
                actionButton={actionButton}
                onActionClick={onActionClick}
                prefixIcon={prefixIcon}
            >
                <div className="w-100">
                    {title && <h5 className={`mb-2 ${titleClassName}`}>{title}</h5>}
                    {children}
                </div>
            </Alert>
        </div>
    );
}

export function useTimedMessage(props: Omit<Props, "shouldShow">) {
    const [shouldShow, setShouldShow] = useState(false);
    const [message, setMessage] = useState<ReactNode>();

    return {
        element: (
            <TimedMessage
                onClose={() => {
                    setShouldShow(false);
                    props.onClose?.();
                }}
                shouldShow={shouldShow}
                {...props}
            >
                {message ?? props.children}
            </TimedMessage>
        ),
        show: (value?: ReactNode) => {
            setShouldShow(true);
            setMessage(value);
        },
    };
}

TimedMessage.defaultProps = {
    seconds: 5,
    onClose: undefined,
    variant: "success" as AlertVariantType,
    title: undefined,
    className: "",
    alertClassName: "",
    titleClassName: "",
    actionText: undefined,
    onActionClick: undefined,
    prefixIcon: undefined,
    children: undefined,
    actionButton: undefined,
};
